import { useDeferredValue, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Search, UserPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { tenantsApi } from '@/api/tenantsApi'
import { usersApi } from '@/api/usersApi'
import { useInvalidateResourceProfiles } from '@/hooks/useResourceCenter'
import { usePermission } from '@/lib/permissions'
import { useTenant } from '@/providers/TenantProvider'

const PAGE = 50

/**
 * Add people to this center so they get a resource profile (the People list is
 * this center's members). They join with NO role, so nothing about what they
 * can open changes — roles stay a Configure → Users decision.
 *
 * Two paths, because the routes differ: the user directory (`GET /users`) is
 * global and superadmin-only, so a superadmin picks from every user; a center
 * admin holding users.manage adds by email (`/members/by-email`), the same
 * split as Configure → Tenants.
 */
export function AddPeopleDialog({
  open,
  onOpenChange,
  memberIds,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  memberIds: Set<string>
}) {
  const { isSuperAdmin } = usePermission()
  const { selectedTenantId, selectedTenant } = useTenant()
  const qc = useQueryClient()
  const invalidateProfiles = useInvalidateResourceProfiles()
  const [search, setSearch] = useState('')
  const [picked, setPicked] = useState<string[]>([])
  const [email, setEmail] = useState('')
  const [added, setAdded] = useState<string | null>(null)

  const term = useDeferredValue(search.trim())
  const users = useQuery({
    queryKey: ['users', 'add-to-center', term],
    queryFn: () => usersApi.list({ take: PAGE, search: term || undefined, sort: 'name:asc' }),
    enabled: open && isSuperAdmin,
  })
  const candidates = (users.data?.items ?? []).filter(u => !memberIds.has(u.id))

  // Bumped on close. reset() doesn't cancel a request in flight, so one that
  // lands after a close must not clear what the next opening has picked.
  const session = useRef(0)
  const done = (sentIn: number, message: string) => {
    void invalidateProfiles()
    void qc.invalidateQueries({ queryKey: ['users'] })
    void qc.invalidateQueries({ queryKey: ['tenants'] })
    if (sentIn !== session.current) return
    setAdded(message)
    setPicked([])
    setEmail('')
  }
  const center = selectedTenant?.name ?? 'this center'
  const addPicked = useMutation({
    mutationFn: (v: { tenantId: string; userIds: string[]; session: number }) =>
      tenantsApi.addMembers(v.tenantId, v.userIds.map(userId => ({ userId }))),
    onSuccess: (_, v) =>
      done(v.session, `Added ${v.userIds.length} ${v.userIds.length === 1 ? 'person' : 'people'} to ${center}.`),
  })
  const addEmail = useMutation({
    mutationFn: (v: { tenantId: string; email: string; session: number }) =>
      tenantsApi.addMemberByEmail(v.tenantId, { email: v.email }),
    onSuccess: (_, v) => done(v.session, `Added ${v.email} to ${center}.`),
  })
  const error = (addPicked.error ?? addEmail.error) as Error | null

  const close = (o: boolean) => {
    if (!o) {
      session.current++
      setSearch('')
      setPicked([])
      setEmail('')
      setAdded(null)
      addPicked.reset()
      addEmail.reset()
    }
    onOpenChange(o)
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className='flex max-h-[88vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl' onOpenAutoFocus={e => e.preventDefault()}>
        <DialogHeader className='border-b px-6 py-5'>
          <div className='flex items-center gap-3'>
            <span className='flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary'>
              <UserPlus className='h-5 w-5' />
            </span>
            <div>
              <DialogTitle>Add people to {selectedTenant?.name ?? 'this center'}</DialogTitle>
              <DialogDescription>
                From Configure → Users. They join with no role, so their access doesn't change.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className='min-h-0 flex-1 space-y-3 overflow-y-auto px-6 py-4'>
          {added && <p className='rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800'>{added}</p>}
          {error && <p className='rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive'>{error.message}</p>}

          {isSuperAdmin ? (
            <>
              <div className='relative'>
                <Search className='pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground' />
                <Input
                  autoFocus
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder='Search users by name or email'
                  aria-label='Search users'
                  className='h-9 rounded-lg pl-8'
                />
              </div>
              {users.error && <p className='text-sm text-destructive'>{users.error.message}</p>}
              {users.isLoading ? (
                <p className='flex items-center gap-2 py-6 text-sm text-muted-foreground'>
                  <Loader2 className='h-4 w-4 animate-spin' /> Loading users…
                </p>
              ) : candidates.length === 0 ? (
                <p className='rounded-xl border border-dashed py-10 text-center text-sm text-muted-foreground'>
                  {search.trim() ? 'No user outside this center matches.' : 'Every user is already in this center.'}
                </p>
              ) : (
                <ul className='divide-y rounded-xl border'>
                  {candidates.map(u => {
                    const on = picked.includes(u.id)
                    return (
                      <li key={u.id}>
                        <label className={cn('flex cursor-pointer items-center gap-3 px-3 py-2.5 hover:bg-muted/40', on && 'bg-primary/[0.04]')}>
                          <Checkbox
                            checked={on}
                            onCheckedChange={v => setPicked(v === true ? [...picked, u.id] : picked.filter(id => id !== u.id))}
                          />
                          <span className='min-w-0 flex-1'>
                            <span className='block truncate text-sm font-medium'>{u.fullName || u.username || u.email}</span>
                            <span className='block truncate text-xs text-muted-foreground'>{u.email}</span>
                          </span>
                          {!u.enabled && <span className='text-xs text-muted-foreground'>disabled</span>}
                          {u.isSuperAdmin && <span className='rounded-full bg-muted px-2 py-0.5 text-[11px]'>super admin</span>}
                          <span className='text-xs text-muted-foreground'>
                            {u.tenantIds.length === 0 ? 'No center' : `${u.tenantIds.length} other center${u.tenantIds.length === 1 ? '' : 's'}`}
                          </span>
                        </label>
                      </li>
                    )
                  })}
                </ul>
              )}
              {(users.data?.total ?? 0) > PAGE && (
                <p className='text-xs text-muted-foreground'>Showing the first {PAGE} — search to narrow.</p>
              )}
            </>
          ) : (
            <form
              className='flex items-end gap-2'
              onSubmit={e => {
                e.preventDefault()
                if (email.trim() && selectedTenantId) addEmail.mutate({ tenantId: selectedTenantId, email: email.trim(), session: session.current })
              }}
            >
              <label className='flex-1 space-y-1'>
                <span className='text-sm font-medium'>Email of an existing user</span>
                <Input
                  type='email'
                  autoFocus
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder='name@hospital.org'
                  className='h-9 rounded-lg'
                />
              </label>
              <Button type='submit' disabled={!email.trim() || !selectedTenantId || addEmail.isPending}>
                {addEmail.isPending && <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />}
                Add
              </Button>
            </form>
          )}
        </div>

        <DialogFooter className='border-t bg-muted/30 px-6 py-4'>
          <Button variant='ghost' onClick={() => close(false)}>
            {added ? 'Done' : 'Cancel'}
          </Button>
          {isSuperAdmin && (
            <Button
              disabled={picked.length === 0 || !selectedTenantId || addPicked.isPending}
              onClick={() =>
                selectedTenantId &&
                addPicked.mutate({ tenantId: selectedTenantId, userIds: picked, session: session.current })
              }
            >
              {addPicked.isPending && <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />}
              Add {picked.length > 0 ? picked.length : ''} {picked.length === 1 ? 'person' : 'people'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
